import React from 'react'

import { ListItem } from '@awazevr/design-system.components.list-item'
import {
  BathBathroom,
  Bed,
  CheckCircleSolid,
  Pet,
  PhotoImagePictureGalleryPic,
  StarSolid,
  UserCouple,
} from '@awazevr/design-system.components.icon'
import { Divider } from '@awazevr/design-system.components.divider'
import { Badge } from '@awazevr/design-system.components.badge'
import { Stack } from '@awazevr/design-system.components.stack'
import { Heading, Text } from '@awazevr/design-system.components.text'
import type { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { CustomMediaGallery } from './CustomMediaGallery'
import { ConditionalWishlistButton } from './ConditionalWishlist'
import { StyledWishlistButtonContainer } from './ConditionalWishlist.styles'
import {
  StyledCustomerMessagingBadge,
  StyledGradeAndRatingContainer,
  StyledIndividualPropertySpec,
  StyledPropertyCardNewDesign,
  StyledPropertySpecs,
  StyledContentAnchor,
  StyledCarouselFloatingElements,
  StyledCarouselContainer,
  StyledKSPItem,
  StyledPricingContainer,
  StyledStarsContainer,
  StyledPrice,
  StyledPriceInfo,
  StyledUrgencyMessaging,
} from './PropertyCardNewDesign.styles'
import { DepositBanner } from '@awazevr/design-system.components.deposit-banner'
import { DEFAULT_IMAGE_URL } from 'src/constants'
import { Image } from '@awazevr/design-system.compositions.image-carousel-overlay'
import { DescriptivePaginationCountContainer } from './CustomMediaGallery.styles'
import { SegmentedControl } from '@awazevr/design-system.components.segmented-control'
import { CurrencyDisplay } from '@awazevr/design-system.utils.currency'
import type { FormattedAmount } from '@awazevr/mfe-common.utils.locale-formatting'
import { IWishlistButtonProps } from '@awazevr/design-system.compositions.property-card/dist/property-card.types'
import { isNovasolBrand, type Deposit } from '@awazevr/mfe-common.configs.brand'
import { usePublicAssetUrl } from '../../utils/helpers'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'

type Nullable<T> = T | null

export interface PropertyCardProps {
  brand: Brand
  code: string
  price: Nullable<number>
  pageUrl: string
  lengthOfStay: number
  onCarouselInteractCallback: (
    event: 'open' | 'close' | 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => void
  onPropertyLinkClick: () => void
  translations: {
    nights: string
    grade: string
    guestRating: string
    studioApt: string
    external: string
    bedrooms: string
    bathrooms: string
    pets: string
    guests: string
    petsFree: string
    from: string
    calloutBadge: string
    outOf: string
    urgencyBadge: string
    depositBanner: {
      reserveTodayForOnly: string
      reserveForSelectedProperties: string
    }
    propertySpecTranslations: {
      bedrooms: {
        singular: string
        plural: string
      }
      bathrooms: {
        singular: string
        plural: string
      }
      pets: {
        singular: string
        plural: string
      }
      studio: string
      externalBathroom: string
      guests: {
        singular: string
        plural: string
      }
    }
    formatCurrency: (value: number) => FormattedAmount
  }
  property: {
    pets: Nullable<number>
    bathrooms: Nullable<number>
    bedrooms: Nullable<number>
    sleeps: Nullable<number>
    includedExtras: Nullable<string[]>
  }
  propertyLocation?: Nullable<string>
  images?: Array<Image>
  wasPrice: Nullable<number>
  fromPrice: Nullable<number>
  propertyName: Nullable<string>
  propertyType: string
  startDate: Nullable<string>
  grade: Nullable<number>
  guestRating: Nullable<number>
  keySellingPoints: Nullable<string[]>
  hasPetsFree: boolean
  hasCallout: boolean
  siteSearch: boolean
  datedSearch?: boolean
  wishlistButton?: IWishlistButtonProps
  activeDepositScheme?: Partial<Deposit> | null
  urgencyMessaging: boolean
}

export const PropertyCardNewDesign: React.FC<PropertyCardProps> = ({
  brand,
  code,
  images,
  wasPrice,
  fromPrice,
  startDate,
  onCarouselInteractCallback,
  onPropertyLinkClick,
  activeDepositScheme,
  lengthOfStay,
  propertyLocation,
  propertyName,
  propertyType,
  translations,
  grade,
  guestRating,
  property,
  keySellingPoints,
  price,
  pageUrl,
  hasPetsFree,
  hasCallout,
  siteSearch,
  datedSearch,
  wishlistButton,
  urgencyMessaging,
}) => {
  const { formatMessage } = useIntlContext()
  const ratingArray = Array.from({ length: 5 })
  const hasIncludedExtras =
    !!property.includedExtras && property.includedExtras.length > 0
  const hasKeySellingPoints = !!keySellingPoints && keySellingPoints.length > 0
  const includedExtrasItems = property.includedExtras?.map((extra) => (
    <StyledKSPItem key={`property-included-${extra}-${code}`}>
      <CheckCircleSolid iconColour="ColorStatusConstructive300" size="small" />
      <Text sizes={200} type="regular">
        {extra}
      </Text>
    </StyledKSPItem>
  ))

  let gradeImagePath

  switch (brand) {
    case 'cottages.com':
      gradeImagePath = '/brand/property-card-icons/CDC.svg'
      break
    case 'hoseasons':
      gradeImagePath = '/brand/property-card-icons/HOS.svg'
      break
    default:
      gradeImagePath = '/brand/property-card-icons/NOV.svg'
      break
  }

  let isDated = siteSearch ? false : datedSearch

  const gradeImageUrl = usePublicAssetUrl(gradeImagePath)

  let bedroomText = translations.propertySpecTranslations.studio

  if (property.bedrooms && property.bedrooms > 1) {
    bedroomText = translations.propertySpecTranslations.bedrooms.plural
  }
  if (property.bedrooms && property.bedrooms === 1) {
    bedroomText = translations.propertySpecTranslations.bedrooms.singular
  }

  let bathroomsText = translations.propertySpecTranslations.externalBathroom

  if (property.bathrooms && property.bathrooms > 1) {
    bathroomsText = translations.propertySpecTranslations.bathrooms.plural
  }
  if (property.bathrooms && property.bathrooms === 1) {
    bathroomsText = translations.propertySpecTranslations.bathrooms.singular
  }

  let petsText = translations.propertySpecTranslations.pets.plural

  if (property.pets && property.pets === 1) {
    petsText = translations.propertySpecTranslations.pets.singular
  }

  let imageContent

  const fromPriceDisplay = fromPrice && translations.formatCurrency(fromPrice)
  const priceDisplay = price && translations.formatCurrency(price)
  const wasPriceDisplay = wasPrice && translations.formatCurrency(wasPrice)

  const getFormattedDepositAmount = () => {
    if (!activeDepositScheme?.amount) return ''

    if (activeDepositScheme.unitType === 'percentage') {
      return `${activeDepositScheme.amount}%`
    }

    let currencyString: string = ''
    const activeDepositAmount = translations.formatCurrency(
      activeDepositScheme.amount,
    )

    if (activeDepositAmount.currencySignPosition === 'before')
      currencyString += activeDepositAmount.currencySign
    currencyString += activeDepositAmount.formattedAmount
    if (activeDepositAmount.currencySignPosition === 'after')
      currencyString += activeDepositAmount.currencySign

    return currencyString
  }

  if (images === undefined || images.length === 0) {
    imageContent = (
      <img
        style={{ width: '100%' }}
        src={DEFAULT_IMAGE_URL}
        alt="Property images coming soon"
      />
    )
  } else if (images.length === 1) {
    imageContent = (
      <div
        style={{
          position: 'relative',
          height: '100%',
        }}
      >
        <img
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
          }}
          src={images[0].src as string}
          alt={images[0].alt || 'Property image'}
        />
        <DescriptivePaginationCountContainer shouldDiplayPaginationText>
          <SegmentedControl
            icon={
              <PhotoImagePictureGalleryPic
                iconColour="ColorThemeLink"
                size="small"
              />
            }
            text={`1/1`}
          />
        </DescriptivePaginationCountContainer>
      </div>
    )
  } else if (images.length > 1) {
    imageContent = (
      <CustomMediaGallery
        onCarouselInteractCallback={onCarouselInteractCallback}
        images={images}
        translations={{
          imageComingSoon: '',
          openGalleryTitle: '',
          previousNavigation: 'Previous',
          nextNavigation: 'Next',
        }}
        uniqueId={code}
      />
    )
  }

  const ariaString = `
    ${propertyName || 'Property'}${propertyLocation ? ` - ${propertyLocation}` : ''}. ${lengthOfStay} ${translations.nights} ${startDate ? startDate : ''}. ${
      fromPriceDisplay
        ? `${translations.from} ${fromPriceDisplay.currencySign}${fromPriceDisplay.formattedAmount}.`
        : ``
    } ${
      !fromPriceDisplay && priceDisplay
        ? `${priceDisplay?.currencySign}${priceDisplay?.formattedAmount}.`
        : ``
    }${property.sleeps ? `${property.sleeps} ${translations.propertySpecTranslations.guests.plural}.` : ''} ${grade ? `${translations.grade} ${grade} ${translations?.outOf} 5.` : ''}
  `

  return (
    <StyledPropertyCardNewDesign
      aria-label={ariaString.trim()}
      href={pageUrl}
      target={'_blank'}
      onClick={onPropertyLinkClick}
    >
      {/* Wishlist button */}

      {/* Media Section */}
      <StyledCarouselContainer>
        <StyledCarouselFloatingElements>
          <StyledWishlistButtonContainer>
            <ConditionalWishlistButton
              wishlistButton={wishlistButton}
              label={formatMessage(
                wishlistButton?.isPropertyInWishlist
                  ? {
                      id: 'wishlist.common.saved',
                      defaultMessage: 'Saved',
                    }
                  : {
                      id: 'wishlist.common.save',
                      defaultMessage: 'Save',
                    },
              )}
            />
          </StyledWishlistButtonContainer>

          {/* customer messaging badge */}
          {hasCallout && (
            <StyledCustomerMessagingBadge>
              <Badge
                colourScheme="reverse"
                variant="solid"
                text={translations.calloutBadge}
              />
            </StyledCustomerMessagingBadge>
          )}
        </StyledCarouselFloatingElements>

        {imageContent}
      </StyledCarouselContainer>

      <StyledContentAnchor>
        {/* Prices Section */}
        <Stack spacing={8}>
          <StyledPricingContainer
            direction={isDated ? 'row-reverse' : 'column'}
          >
            <StyledPriceInfo>
              {startDate && (!fromPrice || isNovasolBrand(brand)) && (
                <>
                  <ListItem size="small">
                    <Text sizes={300} style={{ fontWeight: '500' }}>
                      {startDate}
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text
                      style={{
                        fontSize: '0.6rem',
                        opacity: 0.6,
                        padding: '0 0.125rem',
                      }}
                    >
                      •
                    </Text>
                  </ListItem>
                </>
              )}

              <ListItem size="small">
                <Text
                  sizes={fromPrice ? 200 : 300}
                  style={!fromPrice ? { fontWeight: '500' } : undefined}
                >
                  {lengthOfStay > 0 ? lengthOfStay : 7}&nbsp;
                  {translations?.nights}&nbsp;
                </Text>
                {!isNovasolBrand(brand) && (
                  <Text sizes={200} style={{ textTransform: 'lowercase' }}>
                    {fromPrice && translations?.from}
                  </Text>
                )}
              </ListItem>
            </StyledPriceInfo>
            <StyledPrice>
              <Heading as={'span'} sizes={200}>
                {fromPriceDisplay && (
                  <CurrencyDisplay {...fromPriceDisplay}>
                    {fromPriceDisplay.formattedAmount}
                  </CurrencyDisplay>
                )}

                {!fromPriceDisplay && priceDisplay && (
                  <CurrencyDisplay {...priceDisplay}>
                    {priceDisplay.formattedAmount}
                  </CurrencyDisplay>
                )}
              </Heading>
              {wasPriceDisplay && (
                <Text
                  sizes={200}
                  color="ColorStatusAttention400"
                  style={{ fontWeight: 500, textDecoration: 'line-through' }}
                >
                  <CurrencyDisplay {...wasPriceDisplay}>
                    {wasPriceDisplay.formattedAmount}
                  </CurrencyDisplay>
                </Text>
              )}
            </StyledPrice>
          </StyledPricingContainer>

          {hasIncludedExtras && (
            <div className="tablet-ksp" style={{ marginTop: '12px' }}>
              <Stack
                direction="row"
                spacing={16}
                wrap="wrap"
                justify="start"
                align="center"
              >
                {includedExtrasItems}
              </Stack>
            </div>
          )}
          {hasKeySellingPoints && (
            <div style={{ marginTop: '12px' }} className="tablet-ksp">
              <Stack
                direction="column"
                spacing={16}
                wrap="wrap"
                justify="start"
                align="start"
              >
                {hasPetsFree && (
                  <StyledKSPItem key={`${code}-property-ksp-pets-free`}>
                    <Pet iconColour="ColorBrandSecondary" size="small" />
                    <Text sizes={200} type="regular">
                      {translations.petsFree}
                    </Text>
                  </StyledKSPItem>
                )}
                {keySellingPoints.map((ksp) => (
                  <StyledKSPItem key={`${code}-property-ksp-${ksp}`}>
                    <CheckCircleSolid
                      iconColour="ColorStatusConstructive300"
                      size="small"
                    />
                    <Text sizes={200} type="regular">
                      {ksp}
                    </Text>
                  </StyledKSPItem>
                ))}
              </Stack>
            </div>
          )}
        </Stack>

        <Divider margin={16} />

        <Stack direction="column" spacing={[8, 8, 8]}>
          <Stack direction={'column'} spacing={0}>
            {propertyType && !siteSearch && (
              <Text
                sizes={100}
                style={{ fontWeight: 'bold' }}
                color="ColorStatusInfo500"
              >
                {propertyType}
              </Text>
            )}

            {propertyLocation && (
              <Text
                sizes={300}
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {propertyLocation}
              </Text>
            )}

            {propertyName && (
              <Text
                sizes={200}
                color="ColorGrey600"
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  whiteSpace: 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {propertyName}
              </Text>
            )}
          </Stack>

          {/* Social proof */}
          {(grade || guestRating) && (
            <StyledGradeAndRatingContainer>
              {grade && grade > 0 && (
                <div>
                  <Text sizes={100} type="mid" mb={4}>
                    {translations?.grade ? translations.grade : 'Grade'}
                  </Text>
                  <Stack direction="row" spacing={4} align={'center'}>
                    {ratingArray.map((_, index) => (
                      <StarSolid
                        key={`${code}-grade-star-${index}`}
                        iconColour={
                          index < grade ? 'ColorThemeAccent2' : 'ColorGrey200'
                        }
                        size="x-small"
                      />
                    ))}
                    <Text
                      style={{
                        lineHeight: '1',
                        height: '0.9rem',
                        marginLeft: '0.25rem',
                      }}
                    >
                      <strong>{grade}</strong>/5
                    </Text>
                  </Stack>
                </div>
              )}

              {guestRating && guestRating > 0 && (
                <div>
                  <Text sizes={100} type="mid" mb={4}>
                    {translations?.guestRating
                      ? translations.guestRating
                      : 'Guest Rating'}
                  </Text>

                  <StyledStarsContainer>
                    <Stack direction="row" spacing={4} align={'center'}>
                      <svg
                        width="20"
                        viewBox="0 0 161 78"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M40.2 68.9C25.8 68 14.3 56.5 13.4 42H70.4H76.4V41.3C76.4 16.6 60.6 0 38.8 0C15.7 0 0 16.9 0 38.9C0 59.7 16.2 78 38.3 78C54.4 78 67.1 69.1 74 52.5H67.7C63.1 62.2 53.3 68.9 42 68.9C41.2 69 40.7 69 40.2 68.9Z"
                          fill="#FFDD00"
                        ></path>
                        <path
                          d="M120.098 68.9C134.498 68 145.998 56.5 146.898 42H89.8984H83.8984V41.3C83.8984 16.6 99.6984 0 121.498 0C144.598 0 160.298 16.9 160.298 38.9C160.298 59.7 144.098 78 121.998 78C105.898 78 93.1985 69.1 86.2985 52.5H92.6984C97.2984 62.2 107.098 68.9 118.398 68.9C118.898 69 119.498 69 120.098 68.9Z"
                          fill="#FFDD00"
                        ></path>
                      </svg>
                      <Text
                        style={{
                          lineHeight: '1',
                          height: '0.9rem',
                          marginLeft: '0.25rem',
                        }}
                      >
                        <strong>{guestRating}</strong>/5
                      </Text>
                    </Stack>
                  </StyledStarsContainer>
                </div>
              )}

              {urgencyMessaging && (
                <StyledUrgencyMessaging>
                  <p>{translations.urgencyBadge}</p>
                </StyledUrgencyMessaging>
              )}
            </StyledGradeAndRatingContainer>
          )}

          {(hasIncludedExtras || hasKeySellingPoints) && (
            <div style={{ marginTop: '12px' }} className="mobile-ksp">
              <Stack
                direction="row"
                spacing={16}
                wrap="wrap"
                justify="start"
                align="center"
              >
                {hasIncludedExtras && includedExtrasItems}
                {hasPetsFree && hasKeySellingPoints && (
                  <StyledKSPItem key={`${code}-property-ksp-pets-free`}>
                    <Pet iconColour="ColorBrandSecondary" size="small" />
                    <Text sizes={200} type="regular">
                      {translations.petsFree}
                    </Text>
                  </StyledKSPItem>
                )}
                {hasKeySellingPoints &&
                  keySellingPoints.map((ksp) => (
                    <StyledKSPItem key={`${code}-property-ksp-${ksp}`}>
                      <CheckCircleSolid
                        iconColour="ColorStatusConstructive300"
                        size="small"
                      />
                      <Text sizes={200} type="regular">
                        {ksp}
                      </Text>
                    </StyledKSPItem>
                  ))}
              </Stack>
            </div>
          )}

          {!siteSearch && <Divider margin={16} />}
        </Stack>

        {/* Property Specs */}

        {!siteSearch && (
          <>
            <Divider margin={16} isVertical={true} />
            <StyledPropertySpecs>
              <StyledIndividualPropertySpec>
                <UserCouple iconColour="ColorGrey600" size="small" />
                <Text sizes={100} color="ColorGrey600">
                  <Text
                    as="span"
                    type="bold"
                    sizes={100}
                    color="ColorTextHeadingOnWhite"
                  >
                    {property.sleeps ? property.sleeps : 2}
                  </Text>{' '}
                  {translations.propertySpecTranslations.guests.plural}
                </Text>
              </StyledIndividualPropertySpec>

              <Divider isVertical margin={12} />

              <StyledIndividualPropertySpec>
                <Bed iconColour="ColorGrey600" size="small" />
                <Text sizes={100} color="ColorGrey600">
                  {!!property?.bedrooms && (
                    <>
                      <Text
                        as="span"
                        type="bold"
                        sizes={100}
                        color="ColorTextHeadingOnWhite"
                      >
                        {property?.bedrooms}
                      </Text>{' '}
                    </>
                  )}
                  {bedroomText}
                </Text>
              </StyledIndividualPropertySpec>

              <Divider isVertical margin={12} />

              <StyledIndividualPropertySpec>
                <BathBathroom iconColour="ColorGrey600" size="small" />
                <Text sizes={100} color="ColorGrey600">
                  {!!property?.bathrooms && (
                    <>
                      <Text
                        as="span"
                        type="bold"
                        sizes={100}
                        color="ColorTextHeadingOnWhite"
                      >
                        {property?.bathrooms}
                      </Text>{' '}
                    </>
                  )}
                  {bathroomsText}
                </Text>
              </StyledIndividualPropertySpec>

              <Divider isVertical margin={12} />

              <StyledIndividualPropertySpec>
                <Pet iconColour="ColorGrey600" size="small" />
                <Text sizes={100} color="ColorGrey600">
                  <Text
                    as="span"
                    type="bold"
                    sizes={100}
                    color="ColorTextHeadingOnWhite"
                  >
                    {property?.pets || 0}
                  </Text>{' '}
                  {petsText}
                </Text>
              </StyledIndividualPropertySpec>
            </StyledPropertySpecs>
          </>
        )}
      </StyledContentAnchor>

      {/* Low Deposit */}
      {activeDepositScheme && (
        <DepositBanner
          highlightedMessage={`${translations.depositBanner.reserveTodayForOnly} ${getFormattedDepositAmount()}`}
          termsMessage={
            siteSearch
              ? translations.depositBanner.reserveForSelectedProperties
              : ''
          }
        />
      )}
    </StyledPropertyCardNewDesign>
  )
}

export default PropertyCardNewDesign
